import { useState, useEffect, useCallback, useMemo } from "react";
import {
  ScheduledReview,
  UpcomingReviews,
  UpcomingReviewsArray,
  ReviewSession,
  UpcomingReviewItem,
} from "../src/types/reviews";
import { reviewsUpdateEvent } from "./reviewsUpdateEvent";
import { getStoredUserTimezone } from "../src/utils/dateUtils";
import { api } from "../src/utils/axiosConfig";

export const useReviews = () => {
  const [pendingReviews, setPendingReviews] = useState<ScheduledReview[]>([]);
  const [upcomingReviews, setUpcomingReviews] = useState<UpcomingReviewsArray>(
    []
  );
  const [allUpcomingReviews, setAllUpcomingReviews] = useState<UpcomingReviews>(
    {}
  );
  const [upcomingPagination, setUpcomingPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    pageSize: 5,
  });
  const [pendingPagination, setPendingPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    pageSize: 5,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userTimezone, setUserTimezone] = useState<string>("");

  const fetchPendingReviews = async (page: number = 1, limit: number = 5) => {
    try {
      const { data } = await api.get(`/reviews/pending`, {
        params: { page, limit },
      });
      setPendingReviews(data.pendingReviews || []);
      const pag = data.pagination || {};
      setPendingPagination({
        currentPage: pag.page || page,
        totalPages:
          pag.totalPages || Math.ceil((pag.total || 0) / (pag.limit || limit)),
        totalItems: pag.total || 0,
        pageSize: pag.limit || limit,
      });
      if (!data) throw new Error("Error de carga de revisiones de hoy");
      // Procesar userTimezone de la respuesta
      if (data.userTimezone) {
        setUserTimezone(data.userTimezone);
        localStorage.setItem("userTimezone", data.userTimezone);
      }
    } catch (err) {
      setPendingReviews([]);
      throw err;
    }
  };

  const fetchAllPendingReviewsForSession = async () => {
    try {
      // Si ya tenemos todas las tarjetas cargadas (menos de 5), usarlas directamente
      if (
        pendingReviews.length === totalPendingCount &&
        totalPendingCount > 0
      ) {
        return; // Ya tenemos todas las tarjetas
      }

      // Intentar cargar la primera página con límite alto
      const { data } = await api.get(`/reviews/pending`, {
        params: { page: 1, limit: 50 }, // Límite más alto pero razonable
      });

      if (data && data.pendingReviews) {
        setPendingReviews(data.pendingReviews);

        // Si hay más páginas, cargar las restantes
        if (data.pagination && data.pagination.totalPages > 1) {
          const allReviews = [...data.pendingReviews];

          // Cargar páginas restantes en paralelo
          const promises = [];
          for (let page = 2; page <= data.pagination.totalPages; page++) {
            promises.push(
              api
                .get(`/reviews/pending`, {
                  params: { page, limit: 50 },
                })
                .then((response) => response.data.pendingReviews || [])
            );
          }

          try {
            const additionalPages = await Promise.all(promises);
            additionalPages.forEach((pageReviews) => {
              allReviews.push(...pageReviews);
            });
            setPendingReviews(allReviews);
          } catch (pageError) {
            console.warn("Error cargando páginas adicionales:", pageError);
            // Continuar con las tarjetas ya cargadas
          }
        }

        // Procesar userTimezone
        if (data.userTimezone) {
          setUserTimezone(data.userTimezone);
          localStorage.setItem("userTimezone", data.userTimezone);
        }
      } else {
        throw new Error("No se recibieron datos válidos");
      }
    } catch (err) {
      console.warn(
        "Error cargando todas las tarjetas, manteniendo funcionalidad actual:",
        err
      );
      // Si falla todo, no cambiar pendingReviews para mantener la funcionalidad básica
      // El usuario al menos podrá revisar las 5 tarjetas de la página actual
    }
  };

  const fetchUpcomingReviews = async (
    days: number = 7,
    page: number = 1,
    limit: number = 5
  ) => {
    try {
      const { data } = await api.get(`/reviews/upcoming`, {
        params: { days, page, limit },
      });
      setUpcomingReviews(data.upcomingReviews || []);
      const pag = data.pagination || {};
      setUpcomingPagination({
        currentPage: pag.page || page,
        totalPages:
          pag.totalPages || Math.ceil((pag.total || 0) / (pag.limit || limit)),
        totalItems: pag.total || 0,
        pageSize: pag.limit || limit,
      });

      if (!data)
        throw new Error("Error de carga de revisiones de los próximos días");
      // Procesar userTimezone de la respuesta
      if (data.userTimezone) {
        setUserTimezone(data.userTimezone);
        localStorage.setItem("userTimezone", data.userTimezone);
      }
    } catch (err) {
      setUpcomingReviews([]);
      throw err;
    }
  };

  const fetchAllUpcomingReviews = async () => {
    try {
      const { data } = await api.get(`/reviews/upcoming`, {
        params: { days: 30 },
      });
      setAllUpcomingReviews(data.upcomingReviews || {});
    } catch (err) {
      setAllUpcomingReviews({});
      throw err;
    }
  };

  const fetchAllReviews = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      await Promise.all([
        fetchPendingReviews(),
        fetchUpcomingReviews(7),
        fetchAllUpcomingReviews(),
      ]);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Error de carga de revisiones"
      );
    } finally {
      setLoading(false);
    }
  }, [setLoading, setError]);

  const completeReview = async (
    scheduledReviewId: number,
    difficultyRating: 1 | 2 | 3
  ) => {
    try {
      const { data: result } = await api.post(`/reviews/complete`, {
        scheduledReviewId,
        difficultyRating,
      });

      if (!result) throw new Error("Error al completar la revisión");

      await fetchAllReviews();

      // Evento global de reviewsUpdateEvent - unión de hooks
      // para sincronización del widget del calendario (sin refresh)
      reviewsUpdateEvent.notify();

      return result;
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Error al completar la revisión"
      );
      throw err;
    }
  };

  const rescheduleReview = async (reviewId: number, newDate: string) => {
    try {
      const { data: result } = await api.put(
        `/reviews/reschedule/${reviewId}`,
        {
          newDate,
        }
      );

      if (!result) {
        const errorText = await result.text();
        throw new Error(`Error reprogramando la revisión: ${errorText}`);
      }

      await fetchAllReviews();

      // Evento global de reviewsUpdateEvent - unión de hooks
      // para sincronización del widget del calendario (sin refresh)
      reviewsUpdateEvent.notify();

      return result;
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Error reprogramando la revisión"
      );
      throw err;
    }
  };

  const getGroupedSessions = (): ReviewSession[] => {
    const sessions: ReviewSession[] = [];

    if (Array.isArray(pendingReviews)) {
      pendingReviews.forEach((review) => {
        sessions.push({
          id: review.id,
          type: "pending",
          dueDate: review.dueDate,
          card: review.card,
          intervalDays: review.intervalDays,
        });
      });
    }

    if (Array.isArray(upcomingReviews)) {
      upcomingReviews.forEach((review: UpcomingReviewItem) => {
        sessions.push({
          id: review.id,
          type: "upcoming",
          dueDate: review.dueDate,
          card: review.card,
          intervalDays: review.intervalDays,
        });
      });
    }

    return sessions.sort(
      (a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()
    );
  };

  const upcoming7DaysCount = useMemo(() => {
    // Usar el total real de la paginación, no solo los elementos de la página actual
    return upcomingPagination.totalItems || 0;
  }, [upcomingPagination.totalItems]);

  const totalUpcomingCount = useMemo(() => {
    if (!allUpcomingReviews || typeof allUpcomingReviews !== "object") return 0;
    return Object.values(allUpcomingReviews).reduce((total, dateGroup) => {
      return total + (Array.isArray(dateGroup) ? dateGroup.length : 0);
    }, 0);
  }, [allUpcomingReviews]);

  useEffect(() => {
    fetchAllReviews();
    // Inicializar zona horaria desde localStorage si existe
    const storedTimezone = getStoredUserTimezone();
    if (storedTimezone) {
      setUserTimezone(storedTimezone);
    }
  }, [fetchAllReviews, getStoredUserTimezone, setUserTimezone]);

  // Contador total de pendientes (no paginado)
  const totalPendingCount = useMemo(() => {
    return pendingPagination.totalItems || 0;
  }, [pendingPagination.totalItems]);

  return {
    pendingReviews,
    upcomingReviews,
    allUpcomingReviews,
    upcomingPagination,
    pendingPagination,
    totalPendingCount, // Total real de pendientes
    upcoming7DaysCount,
    totalUpcomingCount,
    loading,
    error,
    userTimezone,
    fetchAllReviews,
    fetchPendingReviews,
    fetchAllPendingReviewsForSession,
    fetchUpcomingReviews,
    fetchAllUpcomingReviews,
    completeReview,
    rescheduleReview,
    getGroupedSessions,
  };
};
